"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

export type ThemeId = "midnight" | "onesolution" | "mk43" | "centralsquare" | "spillman";

export const THEMES: { id: ThemeId; label: string; description: string }[] = [
  { id: "midnight", label: "Midnight", description: "Dark dashboard (default)" },
  { id: "onesolution", label: "OneSolution", description: "Light, retro CAD skin" },
  { id: "mk43", label: "MK43", description: "Black, mint-bordered cards, steel-blue labels" },
  { id: "centralsquare", label: "CentralSquare", description: "Light enterprise CAD: navy chrome, vivid blue actions" },
  { id: "spillman", label: "Spillman Flex", description: "Dark navy CAD: chrome-blue panels, vivid status badges" }
];

const STORAGE_KEY = "cad-theme";
const DEFAULT_THEME: ThemeId = "midnight";

function isThemeId(value: string | null): value is ThemeId {
  return (
    value === "midnight" ||
    value === "onesolution" ||
    value === "mk43" ||
    value === "centralsquare" ||
    value === "spillman"
  );
}

type ThemeContextValue = {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // The inline script in the <head> (see layout.tsx) already set
  // document.documentElement's data-theme attribute before hydration, so we
  // just need to read the same value back into React state here -- this
  // avoids a flash of the wrong theme on load.
  const [theme, setThemeState] = useState<ThemeId>(DEFAULT_THEME);

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeId(stored)) setThemeState(stored);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeId) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage can throw in some embedded/NUI browser contexts --
      // the theme still applies for this session, it just won't persist.
    }
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

/** Plain string (not JSX) so it can be dropped straight into a <script> tag
 *  in the root layout, running before React hydrates. Reads the same
 *  localStorage key ThemeProvider uses, so the two never disagree. */
export const THEME_ANTI_FLASH_SCRIPT = `
(function () {
  try {
    var t = window.localStorage.getItem("${STORAGE_KEY}");
    if (
      t === "onesolution" ||
      t === "midnight" ||
      t === "mk43" ||
      t === "centralsquare" ||
      t === "spillman"
    ) {
      document.documentElement.setAttribute("data-theme", t);
    }
  } catch (e) {}
})();
`;
